/**
 * Scene configuration schemas
 */

import { z } from "zod";

/**
 * Color3 schema (RGB)
 */
export const Color3Schema = z.object({
  b: z.number().min(0).max(1),
  g: z.number().min(0).max(1),
  r: z.number().min(0).max(1),
});

/**
 * Color4 schema (RGBA)
 */
const Color4Schema = z.object({
  a: z.number().min(0).max(1).optional(),
  b: z.number().min(0).max(1),
  g: z.number().min(0).max(1),
  r: z.number().min(0).max(1),
});

/**
 * Vector3 schema
 */
export const Vector3Schema = z.object({
  x: z.number(),
  y: z.number(),
  z: z.number(),
});

/**
 * Scene configuration schema
 */
export const SceneOptionsSchema = z.object({
  ambientColor: Color3Schema.optional(),
  clearColor: Color4Schema.optional(),
  collisionsEnabled: z.boolean().optional(),
  enableOptimizer: z.boolean().optional(),
  enablePhysics: z.boolean().optional(),
  fogColor: Color3Schema.optional(),
  fogDensity: z.number().positive().optional(),
  fogEnd: z.number().optional(),
  fogMode: z.enum(["none", "exp", "exp2", "linear"]).optional(),
  fogStart: z.number().optional(),
  gravity: Vector3Schema.optional(),
  skipFrustumClipping: z.boolean().optional(),
});

/**
 * Scene manager configuration schema
 */
export const SceneManagerConfigSchema = z.object({
  autoOptimize: z.boolean().optional(),
  debug: z.boolean().optional(),
  defaultSceneOptions: SceneOptionsSchema.optional(),
  enableDebugLayer: z.boolean().optional(),
  targetFps: z.number().positive().optional(),
});

export type SceneOptions = z.infer<typeof SceneOptionsSchema>;
export type SceneManagerConfig = z.infer<typeof SceneManagerConfigSchema>;

/**
 * Default configurations (separate from schemas as per CLAUDE.md)
 */
export const defaultSceneOptions: SceneOptions = {
  ambientColor: { b: 1, g: 1, r: 1 },
  clearColor: { a: 1, b: 0.3, g: 0.2, r: 0.2 },
  collisionsEnabled: false,
  enableOptimizer: false,
  enablePhysics: false,
  fogColor: { b: 0.3, g: 0.2, r: 0.2 },
  fogDensity: 0.01,
  fogEnd: 60,
  fogMode: "none",
  fogStart: 20,
  gravity: { x: 0, y: -9.81, z: 0 },
  skipFrustumClipping: true,
};

export const defaultSceneManagerConfig: SceneManagerConfig = {
  autoOptimize: false,
  debug: false,
  defaultSceneOptions: defaultSceneOptions,
  enableDebugLayer: false,
  targetFps: 60,
};

/**
 * Validation helpers
 */
export function validateSceneOptions(input: unknown): SceneOptions {
  return SceneOptionsSchema.parse(input);
}

export function safeValidateSceneOptions(
  input: unknown,
):
  | { success: true; data: SceneOptions }
  | { success: false; error: z.ZodError } {
  const result = SceneOptionsSchema.safeParse(input);
  if (result.success) {
    return { data: result.data, success: true };
  }
  return { error: result.error, success: false };
}

export function validateSceneManagerConfig(input: unknown): SceneManagerConfig {
  return SceneManagerConfigSchema.parse(input);
}

export function safeValidateSceneManagerConfig(
  input: unknown,
):
  | { success: true; data: SceneManagerConfig }
  | { success: false; error: z.ZodError } {
  const result = SceneManagerConfigSchema.safeParse(input);
  if (result.success) {
    return { data: result.data, success: true };
  }
  return { error: result.error, success: false };
}
